import React, { useEffect, useState } from 'react'
import CreditCardInput from 'react-credit-card-input'
import { refreshAccessToken } from '../../utilities/extensions'
import { company, CustomerCouponCode, customerData, CustomerReceiptData, PlaidAuthorization } from '../../utilities/CustomerData'
import { apiURL } from '../../utilities/URLs'
import { useLocation, useNavigate } from 'react-router-dom'
import { PaymentConfirmation, PaymentDeclined } from './PaymentModals'
import { usePlaidLink } from 'react-plaid-link'
import { PlaidAuthorizationModal } from './PlaidAuthorizationModal'

import plaidLogo from '../../assets/plaidLogo.png'
import posthog from 'posthog-js'

export function PaymentCenter() {
  const navigate = useNavigate()
  const location = useLocation()
  const isCollections = location.state?.isCollections ?? false

  const MAX_FAILED_ATTEMPTS = 3
  const LOCKOUT_TIME_MS = 60 * 60 * 1000

  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  })

  const [showCouponCode, setShowCouponCode] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  const [customerCouponCode, setCustomerCouponCode] = useState(customerData.customerCouponCode)
  const [couponError, setCouponError] = useState('')

  const [referralCode, setReferralCode] = useState('')
  const [customerReferralCode, setCustomerReferralCode] = useState(customerData.customerReferralCode)
  const [referralCodeError, setReferralCodeError] = useState('')

  const [totalDiscountAmount, setTotalDiscountAmount] = useState('')

  const [customerInvoice, setCustomerInvoice] = useState(customerData.customerInvoiceData)
  const [errors, setErrors] = useState({})
  const [paymentMethod, setPaymentMethod] = useState('card')
  const [paymentOption, setPaymentOption] = useState('full')

  const [payingInvoice, setPayingInvoice] = useState(false)
  const [confirmPayment, setConfirmPayment] = useState(false)
  const [paymentDeclined, setPaymentDeclined] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [paymentCode, setPaymentCode] = useState('')
  const [retriesRemaining, setRetriesRemaining] = useState(3)

  const [generatedReceipt, setGeneratedReceipt] = useState(new CustomerReceiptData())

  const [plaidAuthorization, setPlaidAuthorization] = useState(new PlaidAuthorization())

  const [plaidLinkToken, setPlaidLinkToken] = useState('')
  const [plaidPublicToken, setPlaidPublicToken] = useState('')
  const { open, ready } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: (public_token, metadata) => {
      setPlaidPublicToken(public_token)
    }
  })

  const handleCardNumberChange = (e) => {
    setFormData({ ...formData, cardNumber: e.target.value })
  }

  const handleCardExpiryChange = (e) => {
    setFormData({ ...formData, expiryDate: e.target.value })
  }

  const handleCardCVCChange = (e) => {
    setFormData({ ...formData, cvv: e.target.value })
  }

  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.value)
  }

  const validateForm = () => {
    const newErrors = {}
    if (paymentMethod === 'card') {
      if (!formData.cardNumber) newErrors.cardNumber = 'Card Number is required'
      if (!formData.expiryDate) newErrors.expiryDate = 'Expiry Date is required'
      if (!formData.cvv) newErrors.cvv = 'CVV is required'
    }
    if (paymentOption === 'partial' && (!formData.paymentAmount || formData.paymentAmount <= 0 || formData.paymentAmount > customerInvoice.invoiceBalanceTotal)) {
      newErrors.paymentAmount = `Amount must be between 0 and ${customerInvoice.invoiceBalanceTotal}`
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setPayingInvoice(true)

    const failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0', 10)
    const lastFailedAttempt = parseInt(localStorage.getItem('lastFailedAttempt') || '0', 10)

    const currentTime = new Date().getTime()

    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      if (currentTime - lastFailedAttempt < LOCKOUT_TIME_MS) {
        setPaymentError(`You have reached the maximum number of failed payment attempts. Please try again in ${new Date(LOCKOUT_TIME_MS - (currentTime - lastFailedAttempt)).getMinutes()} minutes.`)
        setRetriesRemaining(0)
        setPaymentDeclined(true)
        setPayingInvoice(false)
        return
      } else {
        localStorage.removeItem('failedAttempts')
        localStorage.removeItem('lastFailedAttempt')
      }
    }

    if (!validateForm()) {
      setPayingInvoice(false)
      return
    }

    const form = new FormData()
    form.append('paymentInfo', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      currentLeadStageId: customerData.customerLeadData.leadStage.leadStageId,
      customerId: customerData.customerLeadData.leadContact.contactId,
      customerEmail: customerData.customerLeadData.leadContact.contactEmail,
      paymentType: paymentMethod,
      isCollections: isCollections,
      paymentAmount: paymentOption === 'full' ? customerInvoice.invoiceRemainingBalance : formData.paymentAmount,
      customerCreditCardInfo: {
        cardNumber: formData.cardNumber,
        cardExpiryDate: formData.expiryDate,
        cardCVV: formData.cvv
      },
      customerBillingInfo: {
        firstName: customerData.customerLeadData.leadContact.contactFirstName,
        lastName: customerData.customerLeadData.leadContact.contactLastName
      },
      customerInvoiceData: customerData.customerInvoiceData,
      customerReferralCode: customerReferralCode,
      customerCouponCode: customerCouponCode
    }))

    try {
      const payInvoiceResponse = await fetch(`${apiURL}/api/payInvoice`, {
        method: 'POST',
        body: form,
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const payInvoiceData = await payInvoiceResponse.json()
      setPayingInvoice(false)

      if (payInvoiceData.response.error) {
        if (payInvoiceData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleSubmit(e) : navigate('/', { state: { expired: true } })
        } else
          console.error(payInvoiceData.response.error)
      } else {
        if (payInvoiceData.response.paymentResponse === 'Payment Successful!')
          handlePaymentSuccess(payInvoiceData)
        else
          handlePaymentFailure(payInvoiceData)
      }
    } catch (error) {
      console.error('An error occurred while processing the payment:', error)
    }
  }

  const handleMakeInvoicePayment = async () => {
    const failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0', 10)
    const lastFailedAttempt = parseInt(localStorage.getItem('lastFailedAttempt') || '0', 10)

    const currentTime = new Date().getTime()

    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      if (currentTime - lastFailedAttempt < LOCKOUT_TIME_MS) {
        setPaymentError(`You have reached the maximum number of failed payment attempts. Please try again in ${new Date(LOCKOUT_TIME_MS - (currentTime - lastFailedAttempt)).getMinutes()} minutes.`)
        setRetriesRemaining(0)
        setPaymentDeclined(true)
        return
      } else {
        localStorage.removeItem('failedAttempts')
        localStorage.removeItem('lastFailedAttempt')
      }
    }

    setPayingInvoice(true)

    const form = new FormData()
    form.append('paymentInfo', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      currentLeadStageId: customerData.customerLeadData.leadStage.leadStageId,
      invoiceId: customerInvoice.invoiceId,
      customerId: customerData.customerLeadData.leadContact.contactId,
      customerEmail: customerData.customerLeadData.leadContact.contactEmail,
      paymentType: paymentMethod,
      paymentAmount: paymentOption === 'full' ? customerInvoice.invoiceRemainingBalance : formData.paymentAmount,
      customerInvoiceData: customerData.customerInvoiceData,
      customerReferralCode: customerReferralCode,
      customerCouponCode: customerCouponCode
    }))

    const payInvoicePlaidResponse = await fetch(`${apiURL}/api/payInvoiceWithPlaid`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const payInvoicePlaidData = await payInvoicePlaidResponse.json()
    setPayingInvoice(false)

    if (payInvoicePlaidData.response.error) {
      if (payInvoicePlaidData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleMakeInvoicePayment() : navigate('/', { state: { expired: true } })
      } else
        console.error(payInvoicePlaidData.response.error)
    } else {
      if (payInvoicePlaidData.response.paymentResponse === 'Payment Successful!')
        handlePaymentSuccess(payInvoicePlaidData)
      else
        handlePaymentFailure(payInvoicePlaidData)
    }
  }

  const handlePaymentFailure = (payInvoiceData) => {
    const currentTime = new Date().getTime()
    let failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0', 10)
    let lastFailedAttempt = parseInt(localStorage.getItem('lastFailedAttempt') || '0', 10)

    failedAttempts++
    lastFailedAttempt = currentTime

    localStorage.setItem('failedAttempts', failedAttempts)
    localStorage.setItem('lastFailedAttempt', lastFailedAttempt)

    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      setPaymentError('You have reached the maximum number of failed payment attempts. Please try again in 60 minutes.')
      setRetriesRemaining(0)
      setPaymentCode('')
      setPaymentDeclined(true)
    } else {
      setFormData({
        cardNumber: '',
        expiryDate: '',
        cvv: ''
      })
      setPaymentError(payInvoiceData.response.paymentResponse)
      setPaymentCode(payInvoiceData.response.paymentCode)
      setRetriesRemaining(3 - failedAttempts)
      setPaymentDeclined(true)
    }
  }

  const handlePaymentSuccess = (payInvoiceData) => {
    customerData.customerInvoiceData.invoiceRemainingBalance = payInvoiceData.response.invoiceRemainingBalance.invoiceRemainingBalance
    customerInvoice.invoiceRemainingBalance = payInvoiceData.response.invoiceRemainingBalance.invoiceRemainingBalance

    customerData.customerInvoiceData.invoicePaid = payInvoiceData.response.invoiceRemainingBalance.invoicePaid
    customerInvoice.invoicePaid = payInvoiceData.response.invoiceRemainingBalance.invoicePaid

    customerData.customerReceipts.push(new CustomerReceiptData(payInvoiceData.response.receiptInfo))
    setGeneratedReceipt(new CustomerReceiptData(payInvoiceData.response.receiptInfo))

    const newCustomerStageId = isCollections ? ('50b79d95-8c1b-41a3-a92c-654234e7a3e3') : (customerData.customerLeadData.leadStage.leadStageId === 'd195503d-081a-4d94-a413-6e02bb83d40c' ? '01ac55ea-c71f-44df-8b23-3e64685ca01f' : '8465c900-5530-4161-b7be-970918c05988')
    for (const stage of customerData.customerStageHistory) {
      if (stage.currentStage) stage.currentStage = false
      if (stage.customerStageId === newCustomerStageId) stage.currentStage = true
    }

    setConfirmPayment(true)
  }

  const handleGoToDocumentCenter = () => {
    navigate('/dashboard', { state: { activeTab: 1 } })
  }

  const handleCheckCouponCode = async () => {
    const form = new FormData()
    form.append('couponInfo', JSON.stringify({
      customerId: customerData.customerLeadData.leadContact.contactId,
      couponCode: couponCode,
      couponType: customerData.customerInvoiceData.invoiceTerms === 'Invoice Due On Receipt' ? 'Pre-Paid' : 'Post-Paid'
    }))

    const checkCouponResponse = await fetch(`${apiURL}/api/checkCouponCode`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const checkCouponData = await checkCouponResponse.json()
    if (checkCouponData.response.error) {
      if (checkCouponData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleCheckCouponCode() : navigate('/', { state: { expired: true } })
      } else
        setCouponError(checkCouponData.response.error)
    } else {
      setCouponError('')
      setCouponCode('')
      setCustomerCouponCode(new CustomerCouponCode({
        customerId: customerData.customerLeadData.leadContact.contactId,
        couponCode: checkCouponData.response.couponCode.couponCode,
        couponAmount: checkCouponData.response.couponCode.couponAmount,
        couponType: checkCouponData.response.couponCode.couponType
      }))
    }
  }

  const handleCheckReferralCode = async () => {
    if (referralCode === customerReferralCode.referralCode)
      setReferralCodeError('Cannot Use Own Referral Code.')
    else {
      const checkReferralCodeResponse = await fetch(`${apiURL}/api/checkReferralCode?customerId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}&referralCode=${encodeURIComponent(referralCode)}&isCheckout=1`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const checkReferralCodeData = await checkReferralCodeResponse.json()
      if (checkReferralCodeData.response.error) {
        if (checkReferralCodeData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleCheckReferralCode() : navigate('/', { state: { expired: true } })
        } else
          setReferralCodeError(checkReferralCodeData.response.error)
      } else {
        setReferralCodeError('')
        setReferralCode('')
        setCustomerReferralCode(prevReferralCode => (
          {
            ...prevReferralCode,
            appliedReferralCode: checkReferralCodeData.response.referralCode
          }
        ))
      }
    }
  }

  useEffect(() => {
    const handleGetLinkToken = async () => {
      const linkTokenResponse = await fetch(`${apiURL}/api/createPlaidLinkToken?customerId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const linkTokenData = await linkTokenResponse.json()
      if (linkTokenData.response.error) {
        if (linkTokenData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetLinkToken() : navigate('/', { state: { expired: true } })
        } else
          console.error(linkTokenData.response.error)
      } else
        setPlaidLinkToken(linkTokenData.response.linkToken)
    }

    posthog.startSessionRecording()
    document.title = `${company.companyName} | Payment Portal`
    handleGetLinkToken()

    return () => {
      posthog.stopSessionRecording()
    }
  }, [])

  useEffect(() => {
    const handleExchangePublicToken = async () => {
      const exchangePublicTokenResponse = await fetch(`${apiURL}/api/exchangePublicToken?publicToken=${encodeURIComponent(plaidPublicToken)}&customerId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const exchangePublicTokenData = await exchangePublicTokenResponse.json()
      if (exchangePublicTokenData.response.error) {
        if (exchangePublicTokenData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleExchangePublicToken() : navigate('/', { state: { expired: true } })
        } else
          console.error(exchangePublicTokenData.response.error)
      } else
        setPlaidAuthorization(new PlaidAuthorization(exchangePublicTokenData.response))
    }

    if (plaidPublicToken.length > 0)
      handleExchangePublicToken()
  }, [plaidPublicToken])

  const getNewInvoiceBalance = () => {
    if (customerCouponCode.couponAmount > 0 || customerReferralCode.numHaveRefund > 0 || customerReferralCode.appliedReferralCode.length > 0) {
      let discountAmount = 0
      if (customerCouponCode.couponAmount > 0)
        discountAmount += customerCouponCode.couponAmount > 1 ? customerCouponCode.couponAmount : (customerInvoice.invoiceRemainingBalance * customerCouponCode.couponAmount).toFixed(2)
      if (customerReferralCode.numHaveRefund > 0)
        discountAmount += customerReferralCode.numHaveRefund * 50
      if (customerReferralCode.appliedReferralCode.length > 0)
        discountAmount += 50

      if (Number((customerInvoice.invoiceBalanceTotal - discountAmount).toFixed(2)) > 0)
        setTotalDiscountAmount((customerInvoice.invoiceBalanceTotal - parseFloat(discountAmount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
      else
        setTotalDiscountAmount(0)
    }
  }

  useEffect(() => {
    getNewInvoiceBalance()
  }, [customerCouponCode, customerReferralCode])

  return (
    <>
      <div className='flex justify-center w-full'>
        <div className='flex flex-col items-center'>
          <p onClick={() => setShowCouponCode(!showCouponCode)} className='w-full p-4 text-white mt-10 rounded-lg shadow-lg cursor-pointer bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
            Have a coupon or referral code? Click here to enter your code
          </p>
          <div className='p-4 w-full h-fit flex lg:flex-row flex-col mb-10 justify-center gap-4 font-mont'>
            <div className='mb-6'>
              <h3 className='text-xl font-medium mb-4'>Invoice Details</h3>
              <div className='border p-6 rounded-md bg-gray-50'>
                <p className='text-lg font-semibold mb-2'>Invoice Info:</p>
                <p className='text-sm mb-2'><strong>Invoice #:</strong> {customerInvoice.invoiceNumber}</p>
                <p className='text-sm mb-2'><strong>Description:</strong> {customerInvoice.invoiceDescription}</p>
                <p className='text-sm mb-2'><strong>Refund:</strong> ${customerInvoice.invoiceRefund.toLocaleString()}</p>
                <p className='text-sm mb-2'><strong>Rate:</strong> {Math.round(Number(customerInvoice.invoiceRate) * 100)}%</p>
                <p className='text-sm mb-2'><strong>Sub Total:</strong> ${customerInvoice.invoiceSubTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className='text-sm mb-2'><strong>Tax:</strong> ${customerInvoice.invoiceTax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                {totalDiscountAmount.length === 0 ? (
                  <div>
                    <div className='mb-4'>
                      <p className='text-sm mb-2'>
                        <strong>Balance Total:</strong> ${customerInvoice.invoiceBalanceTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className='text-sm mb-2'>
                        <strong>Remaining Balance Total:</strong> ${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className='text-xl mb-4'>
                        <strong>Paying Today: ${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className='mb-10'>
                    <div className='mb-4'>
                      <p className='text-sm mb-2'>
                        <strong>Balance Total:</strong>
                        <span className='line-through text-gray-500 ml-2'>${customerInvoice.invoiceBalanceTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className='ml-4 text-green-600'>${totalDiscountAmount}</span>
                      </p>
                      <p className='text-sm mb-2'>
                        <strong>Remaining Balance Total:</strong>
                        <span className='line-through text-gray-500 ml-2'>${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className='ml-4 text-green-600'>${totalDiscountAmount}</span>
                      </p>
                      <p className='text-xl mb-4'>
                        <strong>Paying Today:</strong>
                        <span className='line-through text-gray-500 ml-2 font-bold'>${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className='ml-4 text-green-600 font-bold'>${totalDiscountAmount}</span>
                      </p>
                    </div>

                    {customerReferralCode.numHaveRefund > 0 && (
                      <p className='text-sm text-gray-700 mb-2'>
                        <span className='font-semibold'>Referral Code Usage:</span> {customerReferralCode.numHaveRefund} -
                        <span className='text-green-600 ml-2'>-${customerReferralCode.numHaveRefund * 50}</span>
                      </p>
                    )}

                    {customerCouponCode.couponAmount > 0 && (
                      <p className='text-sm text-gray-700 mb-2'>
                        <span className='font-semibold'>Applied Coupon Amount:</span>
                        <span className='text-green-600 ml-2'>-${customerCouponCode.couponAmount > 1 ? customerCouponCode.couponAmount : (customerInvoice.invoiceRemainingBalance * customerCouponCode.couponAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </p>
                    )}

                    {customerReferralCode.appliedReferralCode.length > 0 && (
                      <p className='text-sm text-gray-700 mb-2'>
                        <span className='font-semibold'>Applied Referral Code:</span>
                        <span className='text-green-600 ml-2'>-$50</span>
                      </p>
                    )}
                  </div>
                )}

                {showCouponCode && (
                  <div className='flex flex-col gap-4'>
                    <div className='bg-blue-50 shadow-lg rounded-md p-4'>
                      <label htmlFor='couponCode' className='block text-sm font-medium text-gray-700 mb-2'>
                        Apply Coupon Code
                      </label>
                      {couponError && <p className='text-red-500 text-xs mt-2'>{couponError}</p>}
                      {customerCouponCode.couponCode.length > 0 ? (
                        <div className='flex flex-row gap-4 items-center mt-4'>
                          <p>Applied Coupon Code:</p>
                          <p className='font-bold'>{customerCouponCode.couponCode}</p>
                        </div>
                      ) : (
                        <div className='flex items-center'>
                          <input
                            type='text'
                            id='couponCode'
                            name='couponCode'
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            className='p-2 border border-gray-300 rounded-l-md w-full'
                            placeholder='Enter coupon code'
                          />
                          <button
                            onClick={() => handleCheckCouponCode()}
                            className={`text-white p-2 rounded-r-md ${couponCode.length === 0 ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 transition-colors'}`}
                            disabled={couponCode.length === 0}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                    </div>
                    <div className='bg-blue-50 shadow-lg rounded-md p-4'>
                      <label htmlFor='couponCode' className='block text-sm font-medium text-gray-700 mb-2'>
                        Apply Referral Code
                      </label>
                      {referralCodeError && <p className='text-red-500 text-xs mt-2'>{referralCodeError}</p>}
                      {customerReferralCode.appliedReferralCode.length > 0 ? (
                        <div className='flex flex-row gap-4 items-center mt-4'>
                          <p>Applied Referral Code:</p>
                          <p className='font-bold'>{customerReferralCode.appliedReferralCode}</p>
                        </div>
                      ) : (
                        <div className='flex items-center'>
                          <input
                            type='text'
                            id='couponCode'
                            name='couponCode'
                            value={referralCode}
                            onChange={(e) => setReferralCode(e.target.value)}
                            className='p-2 border border-gray-300 rounded-l-md w-full'
                            placeholder='Enter referral code'
                          />
                          <button
                            onClick={() => handleCheckReferralCode()}
                            className={`text-white p-2 rounded-r-md ${referralCode.length === 0 ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 transition-colors'}`}
                            disabled={referralCode.length === 0}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='max-w-md p-6 bg-white shadow-lg rounded-lg h-fit'>
              <h2 className='text-2xl font-semibold mb-6'>Payment Information</h2>
              {paymentMethod === 'card' ? (
                <>
                  <div className='mb-4 flex flex-col items-center'>
                    <label htmlFor='cardNumber' className='text-sm font-medium text-gray-700 text-start w-full'>Card Number</label>
                    <CreditCardInput
                      cardNumberInputProps={{
                        value: formData.cardNumber,
                        onChange: handleCardNumberChange,
                      }}
                      cardExpiryInputProps={{
                        value: formData.expiryDate,
                        onChange: handleCardExpiryChange,
                      }}
                      cardCVCInputProps={{
                        value: formData.cvv,
                        onChange: handleCardCVCChange,
                      }}
                      fieldClassName='mt-2 p-2 border border-gray-300 rounded-md'
                    />
                    <div className='flex gap-4'>
                      {errors.cardNumber && <p className='text-red-500 text-xs mt-1'>{errors.cardNumber}</p>}
                      {errors.expiryDate && <p className='text-red-500 text-xs mt-1'>{errors.expiryDate}</p>}
                      {errors.cvv && <p className='text-red-500 text-xs mt-1'>{errors.cvv}</p>}
                    </div>
                  </div>
                  <div className='mb-4'>
                    <button
                      type='submit'
                      onClick={handleSubmit}
                      className='w-full bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white p-2 rounded-md mt-2'
                      disabled={payingInvoice}
                    >
                      {payingInvoice ? 'Processing...' : 'Submit Payment'}
                    </button>
                  </div>
                </>
              ) : (
                <div className='w-full flex flex-col items-center gap-4'>
                  <img className='w-32' src={plaidLogo} />
                  <button
                    type='submit'
                    onClick={() => open()}
                    className={`w-full text-white p-2 rounded-md mt-2 ${!ready || payingInvoice ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}
                    disabled={!ready || payingInvoice}
                  >
                    Connect Bank Account
                  </button>
                  {payingInvoice && (
                    <div className='flex flex-row items-center justify-center gap-2'>
                      <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8 mt-4 mb-4'></div>
                      <p>Paying Invoice, Please do not leave or refresh this page...</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <PaymentConfirmation
        isOpen={confirmPayment}
        onClose={handleGoToDocumentCenter}
        _customerReceipt={generatedReceipt}
        isShippo={false}
      />
      <PaymentDeclined
        isOpen={paymentDeclined}
        setIsOpen={setPaymentDeclined}
        paymentError={paymentError}
        paymentCode={paymentCode}
        retriesRemaining={retriesRemaining}
      />
      {plaidAuthorization.accessToken && (
        <PlaidAuthorizationModal
          _plaidAuthorization={plaidAuthorization}
          paymentAmount={totalDiscountAmount ? totalDiscountAmount : customerInvoice.invoiceRemainingBalance}
          handleAgreement={handleMakeInvoicePayment}
        />
      )}
    </>
  )
}